// Ollama Provider (Unified Interface)
// Provides local LLM inference via Ollama
// Uses native /api/chat endpoint with think:false to disable thinking mode overhead

/**
 * Convert unified messages to Ollama native format
 */
const convertMessages = (messages) => {
  return messages.map((msg) => {
    if (msg.role === 'tool') {
      return { role: 'tool', content: msg.content, tool_call_id: msg.toolCallId }
    }

    const converted = { role: msg.role, content: msg.content || '' }

    if (msg.toolCalls && msg.toolCalls.length > 0) {
      converted.tool_calls = msg.toolCalls.map((tc) => ({
        id: tc.id,
        type: tc.type || 'function',
        function: {
          name: tc.function.name,
          arguments:
            typeof tc.function.arguments === 'string'
              ? tc.function.arguments
              : JSON.stringify(tc.function.arguments),
        },
      }))
    }

    return converted
  })
}

/**
 * Convert unified tools to OpenAI-compatible format (used by native API too)
 */
const convertTools = (tools) => {
  if (!tools || tools.length === 0) return undefined

  return tools.map((tool) => ({
    type: 'function',
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
    },
  }))
}

/**
 * Parse tool call arguments safely
 */
const parseToolArguments = (argsString) => {
  try {
    return typeof argsString === 'string' ? JSON.parse(argsString) : argsString
  } catch (error) {
    console.warn('[Ollama] Failed to parse tool arguments:', argsString)
    return {}
  }
}

/**
 * Create Ollama adapter
 * @param {Object} ollamaClient - Ollama client instance
 * @returns {Object} Adapter with chat, listModels, checkAvailability methods
 */
export function createOllamaAdapter(ollamaClient) {
  const chat = async ({ apiKey, model, messages, tools, config = {} }) => {
    const host = config.host || process.env.OLLAMA_HOST || 'localhost:11434'

    try {
      const response = await ollamaClient.chat({
        host,
        model,
        messages: convertMessages(messages),
        tools: convertTools(tools),
        think: false,
        options: {
          temperature: config.temperature || 0.7,
          num_predict: config.maxTokens || 2000,
        },
        stream: false,
      })

      const msg = response.message
      if (!msg) {
        throw new Error('Ollama returned no message')
      }

      const toolCalls = msg.tool_calls?.map((tc) => ({
        id: tc.id || `call_${Math.random().toString(36).slice(2, 11)}`,
        type: tc.type || 'function',
        function: {
          name: tc.function.name,
          arguments: parseToolArguments(tc.function.arguments),
        },
      }))

      const finishReason = msg.tool_calls?.length > 0 ? 'tool_calls' : 'stop'

      return {
        message: {
          role: 'assistant',
          content: msg.content || '',
          toolCalls: toolCalls || [],
          finishReason,
          usage: {
            promptTokens: response.prompt_eval_count || 0,
            completionTokens: response.eval_count || 0,
            totalTokens: (response.prompt_eval_count || 0) + (response.eval_count || 0),
          },
        },
        raw: response,
      }
    } catch (error) {
      throw new Error(`Ollama chat failed: ${error.message}`)
    }
  }

  const listModels = async ({ apiKey, config = {} } = {}) => {
    const host = config?.host || process.env.OLLAMA_HOST

    try {
      const response = await ollamaClient.listModels({ host })
      const models = response.models || []

      return models.map((m) => ({
        id: m.name,
        name: m.name,
        size: m.size,
        modified: m.modified_at,
        details: m.details,
      }))
    } catch (error) {
      throw new Error(`Failed to list Ollama models: ${error.message}`)
    }
  }

  const checkAvailability = async (config = {}) => {
    const host = config?.host || process.env.OLLAMA_HOST
    return await ollamaClient.checkAvailability(host)
  }

  return { chat, listModels, checkAvailability }
}
